import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PageHero, SectionHeading, DisclaimerBar } from "../components/shared/Primitives";
import { Calculator, ArrowRight, Info } from "lucide-react";

const SECTIONS = [
  {
    id: "about",
    eyebrow: "About Just Care Community",
    title: "What JCC is — and what JCC isn't.",
    accent: "teal",
    items: [
      {
        q: "What is Just Care Community?",
        a: "Just Care Community is a platform designed to make independent care feel clearer, fairer and less overwhelming. We provide guidance, tools, templates and practical support for self-employed carers and families arranging care directly.",
      },
      {
        q: "Is JCC a care agency?",
        a: "No. JCC is not a care agency and does not provide or manage care. We do not employ carers, assign carers to families, take control of care arrangements, or take a commission from care fees.",
      },
      {
        q: "Does JCC choose a carer for my family?",
        a: "No. Families remain in control of who they speak to, meet and choose. JCC may provide tools that help families and carers find one another, but the final decision and agreement always remain between the people involved.",
      },
      {
        q: "Does JCC set carers' rates?",
        a: "No. Carers decide their own rates based on their experience, responsibilities, working pattern, location and business costs. JCC calculators can help carers work out a sustainable starting point, but they are not a rate-setting service.",
      },
      {
        q: "Is JCC CQC registered?",
        a: "JCC is not set up as a CQC-registered care provider. We do not deliver personal care, supervise carers or take responsibility for care delivery. Whether a particular service or arrangement requires registration depends on the activities actually being carried out. Anyone unsure should seek independent regulatory advice.",
      },
    ],
  },
  {
    id: "families",
    eyebrow: "Questions from Families",
    title: "Planning independent care for someone you love.",
    accent: "teal",
    items: [
      {
        q: "How do I begin looking for independent care?",
        a: "Start by thinking about the practical reality of the support needed. This may include the times care is required, whether the role is live-in or visiting care, mobility needs, companionship, household support, driving, pets, overnight needs and the personality that may suit your relative best. You do not need every answer immediately. A clear starting outline is enough to begin.",
      },
      {
        q: "Does JCC guarantee that I will find a carer?",
        a: "No. JCC cannot guarantee availability, compatibility or that an arrangement will be right for every family. Independent care works best when families take time to communicate clearly, ask questions and make decisions carefully.",
      },
      {
        q: "What should I check before agreeing to work with a carer?",
        a: "Families should carry out their own checks before entering an arrangement. Depending on the role, this may include checking identity, experience, references, insurance, relevant training, DBS information where appropriate and suitability for the specific needs of the person receiving care. It is also sensible to meet or speak properly before agreeing anything.",
      },
      {
        q: "Who agrees the hours, duties and rate?",
        a: "The family and carer agree these directly. A good arrangement should make clear what support is included, the agreed working hours, rest breaks, overnight expectations, payment arrangements, travel, meals, notice periods and what happens if needs change.",
      },
      {
        q: "Is independent care always cheaper than agency care?",
        a: "Not always — care needs vary too much for one answer to fit everyone. However, direct arrangements can often reduce the additional layers of agency overhead and commission. The most important thing is that the arrangement is safe, realistic, sustainable and fair for everyone involved.",
      },
      {
        q: "Can JCC help with live-in care planning?",
        a: "Yes. JCC provides guidance and calculators to help families think through the likely structure and cost of live-in care. Live-in care should never be treated as 24 paid working hours a day. Active care hours, rest, sleep arrangements, overnight disruption and the complexity of the role all need to be discussed openly.",
      },
      {
        q: "What happens if my relative's care needs change?",
        a: "Care needs can change gradually or suddenly. When they do, it is important to review the arrangement rather than expecting the carer to simply absorb more responsibility. A change may include increased hours, mobility changes, falls, continence needs, overnight waking, supervision needs, additional household members or a new care location. JCC provides tools to help families and carers record and discuss changes clearly.",
      },
      {
        q: "What is Family Care Office?",
        a: "Family Care Office is JCC's deeper support space for families arranging independent care. It includes fuller guidance, practical resources and access to relevant JCC arrangement tools as they become available. It does not guarantee that a carer will be found.",
      },
    ],
  },
  {
    id: "carers",
    eyebrow: "Questions from Carers",
    title: "Building a sustainable independent business.",
    accent: "purple",
    items: [
      {
        q: "Who is JCC for?",
        a: "JCC is for experienced carers who want more independence, fairer pay, clearer systems and practical support while building a self-employed care business.",
      },
      {
        q: "Can I use JCC if I currently work for an agency?",
        a: "You can use JCC's general guidance and resources, but you must respect any obligations in your existing employment or agency contract. Do not use JCC to approach, solicit or remove agency clients in breach of an agreement.",
      },
      {
        q: "How do I know what to charge?",
        a: "Your rate needs to reflect more than the time spent directly with a client. Consider your experience, skills, responsibilities, travel, insurance, unpaid admin, training, gaps between placements, pension planning, tax, National Insurance and the reality of the hours you are expected to work. The JCC pricing calculators are designed to help you work this out more realistically.",
      },
      {
        q: "Am I paid for 24 hours a day in a live-in placement?",
        a: "No. A live-in placement should not automatically be treated as 24 paid working hours every day. The arrangement should clearly distinguish active working time, breaks, sleep arrangements, overnight support and what happens when nights become regularly disrupted.",
      },
      {
        q: "Do I need insurance?",
        a: "Independent carers should consider appropriate insurance for the work they provide. What is suitable depends on the services offered and the individual arrangement. Public liability insurance is commonly considered, and professional indemnity may also be relevant in some circumstances. Seek independent advice if unsure.",
      },
      {
        q: "Do I need a DBS check?",
        a: "The right level and route for a DBS check can depend on the work and arrangement involved. Families may reasonably ask about DBS information, but a DBS check is only one part of safer recruitment. References, experience, communication and suitability all matter too.",
      },
      {
        q: "Am I automatically self-employed because I call myself self-employed?",
        a: "No. Employment status depends on the real working relationship, not just the label used. Your contract, working pattern, level of control, ability to accept work and the reality of the arrangement all matter. Take proper advice where you are unsure.",
      },
      {
        q: "Do I need a written agreement?",
        a: "A written agreement is strongly recommended. It helps avoid misunderstandings around duties, rates, payment dates, breaks, meals, travel, overnight expectations, notice, cancellations and changes to the care arrangement.",
      },
      {
        q: "What is Pro Carer Office?",
        a: "Pro Carer Office is JCC's deeper practical support membership for carers. It includes expanded guides, editable tools, templates and resources designed to help carers run their work more professionally and sustainably.",
      },
      {
        q: "Do you help carers work out what to charge?",
        a: "Yes. Pro Carer Office members have access to practical hourly-care and live-in-care pricing calculators. These tools help carers consider their time, expenses, travel, mileage, administration and the realities of self-employed work before setting a rate.",
        cta: { label: "Explore Pro Carer Office", to: "/pro-carer-circle", testid: "faq-hub-cta-explore-pro-carer" },
      },
    ],
  },
  {
    id: "private-rooms",
    eyebrow: "Private Rooms and Care Arrangements",
    title: "A shared space for practical decisions — not for medical records.",
    accent: "teal",
    items: [
      {
        q: "What is a JCC Private Room?",
        a: "A Private Room is a shared online space for a family and a carer to discuss and organise the practical details of a potential or agreed arrangement. It is designed for communication, agreed terms, payment and cancellation arrangements, practical decisions and confirmed changes.",
      },
      {
        q: "Is a Private Room a care-record system?",
        a: "No. It is not a clinical care-record system, emergency system or replacement for professional care documentation. Families and carers should keep their own copies of important agreements and records.",
      },
      {
        q: "What can be stored in a Private Room?",
        a: "At this stage, Private Rooms are for non-sensitive practical arrangement information only. This may include agreed terms, payment arrangements, cancellation arrangements, practical schedules, non-sensitive documents and confirmed decisions.",
      },
      {
        q: "What should not be uploaded to a Private Room?",
        a: "Please do not upload medical records, medication charts, care plans, diagnoses, clinical photographs, NHS information, identity documents, bank details or other sensitive personal information.",
      },
      {
        q: "Can I use a Private Room in an emergency?",
        a: "No. Private Rooms should never be relied on for urgent care, medical emergencies, safeguarding emergencies or immediate decision-making. Use the appropriate emergency, medical or safeguarding service instead.",
      },
    ],
  },
  {
    id: "calculators",
    eyebrow: "JCC Calculators",
    title: "Estimates for planning — not exact quotes.",
    accent: "purple",
    items: [
      {
        q: "Are calculator results exact quotes?",
        a: "No. JCC calculators provide estimates based on the information entered. They are designed to support planning and conversations, not replace professional tax, financial, legal or employment advice.",
      },
      {
        q: "What does the Carer Pricing Calculator help with?",
        a: "It helps carers think about the income they need, likely tax and National Insurance, business costs, unpaid time and realistic working hours. The aim is to help carers avoid setting a rate that looks acceptable on paper but is not sustainable in real life.",
      },
      {
        q: "What does the Live-In Care Rate Calculator help with?",
        a: "It helps carers work through a live-in daily rate using active working hours, business costs, overnight disruption and role complexity. It is designed to reinforce the fact that live-in care is not automatically a 24-hour paid shift.",
      },
      {
        q: "What does the Live-In Care Savings Calculator help with?",
        a: "It helps families compare an agency care cost with an estimated direct-care arrangement. It is a planning tool only. Real costs depend on care needs, working hours, overnight support, complexity, location and the agreement reached with the carer.",
      },
      {
        q: "Will JCC tell me what rate I should charge or pay?",
        a: "No. JCC provides tools and guidance, but the final rate must be agreed directly between the family and carer. A sustainable arrangement should be fair to both sides.",
      },
    ],
  },
];

// Featured Pro Carer Circle tool surfaced on the public hub page.
// This is intentionally NOT a direct link to the toolkit route — the
// toolkit itself is Pro Carer Circle content. The card upsells the
// membership and points visitors at the public Pro Carer Circle page,
// which is available to non-members without any login or access gate.
const PRO_CARER_TOOL = {
  title: "Shift Toolkit",
  body: "Professional editable templates and practical records to help independent carers stay organised, communicate clearly and work with confidence.",
  badge: "Available with Pro Carer Office",
  ctaLabel: "Explore Pro Carer Office",
  ctaTo: "/pro-carer-circle",
  testid: "faq-hub-tool-shift-toolkit",
};

// CTAs at the foot of the page.
const CTAS = [
  { label: "Explore Free Carer Access", to: "/for-carers", accent: "teal" },
  { label: "Explore Free Family Care Office", to: "/family-access", accent: "teal" },
  { label: "View Pro Carer Office", to: "/pro-carer-circle", accent: "purple" },
  { label: "View Family Care Office", to: "/family-access", accent: "purple" },
  { label: "Contact JCC", to: "/contact", accent: "outline" },
];

function CtaLink({ label, to, accent }) {
  const cls =
    accent === "purple"
      ? "btn-purple"
      : accent === "outline"
      ? "btn-outline"
      : "btn-teal";
  const testid = `faq-hub-cta-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
  return (
    <Link to={to} className={`${cls} text-sm`} data-testid={testid}>
      {label}
    </Link>
  );
}

export default function FaqCalculatorHub() {
  return (
    <div data-testid="page-faq-calculator-hub">
      <PageHero
        eyebrow="JCC FAQ + Calculator Hub"
        title="Clear answers, practical guidance and simple planning tools for independent care."
        description="Whether you are a family exploring care options or a carer building an independent business, this hub is here to help you understand the next step."
        accent="teal"
        bgTint="light"
      >
        <a href="#families" className="btn-teal" data-testid="faq-hub-jump-families">
          Questions from Families
        </a>
        <a href="#carers" className="btn-outline" data-testid="faq-hub-jump-carers">
          Questions from Carers
        </a>
      </PageHero>

      {/* Important information / Disclaimer */}
      <section className="section">
        <div className="container-jcc max-w-4xl">
          <DisclaimerBar />
          <div className="card-base p-6 md:p-8 mt-6" data-testid="faq-hub-important-info">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-teal shrink-0 mt-1" strokeWidth={1.75} />
              <div>
                <h2 className="font-display text-xl md:text-2xl font-semibold">Important information</h2>
                <p className="mt-3 text-sm md:text-base text-ink-muted leading-relaxed">
                  Just Care Community provides guidance, resources and practical tools. We do not provide care, employ carers, set rates, supervise day-to-day care, or make decisions on behalf of families or carers.
                </p>
                <p className="mt-3 text-sm md:text-base text-ink-muted leading-relaxed">
                  JCC content is for general guidance and planning only. It is not legal, tax, employment, insurance, medical, clinical or safeguarding advice. Families and carers remain responsible for their own decisions, checks, agreements, insurance, record-keeping and compliance.
                </p>
                <p className="mt-3 text-sm md:text-base text-ink-muted leading-relaxed">
                  JCC is not an emergency service. In an emergency, contact 999 or the appropriate healthcare, safeguarding or local authority service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ sections */}
      {SECTIONS.map((section) => (
        <section
          key={section.id}
          id={section.id}
          className="section border-t border-slate-100"
          data-testid={`faq-hub-section-${section.id}`}
        >
          <div className="container-jcc max-w-4xl">
            <SectionHeading
              eyebrow={section.eyebrow}
              title={section.title}
              accent={section.accent}
            />
            <Accordion
              type="single"
              collapsible
              className="mt-10 rounded-2xl border border-slate-200 bg-white divide-y divide-slate-200"
              data-testid={`faq-hub-accordion-${section.id}`}
            >
              {section.items.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`${section.id}-${i}`}
                  className="border-none px-4 md:px-6"
                >
                  <AccordionTrigger
                    className="text-left text-base md:text-lg font-display font-semibold hover:no-underline py-5"
                    data-testid={`faq-hub-q-${section.id}-${i}`}
                  >
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-ink-muted text-sm md:text-base leading-relaxed pb-5"
                    data-testid={`faq-hub-a-${section.id}-${i}`}
                  >
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      ))}

      {/* Tools & Calculators */}
      <section id="tools" className="section bg-surface-family border-t border-slate-100">
        <div className="container-jcc max-w-5xl">
          <SectionHeading
            eyebrow="Practical Tools & Resources"
            title="Practical tools you can use today."
            accent="teal"
            description="Estimates and planning tools designed to support clearer conversations between families and carers. All results are guidance only, not exact quotes."
          />

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div
              className="card-base p-6 flex flex-col"
              data-testid={PRO_CARER_TOOL.testid}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple flex items-center justify-center shrink-0">
                  <Calculator className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <span
                    className="inline-block text-[11px] font-semibold uppercase tracking-wide
                               text-purple bg-purple-50 rounded-full px-2.5 py-1"
                    data-testid="faq-hub-tool-shift-toolkit-badge"
                  >
                    {PRO_CARER_TOOL.badge}
                  </span>
                  <h3 className="mt-3 font-display font-semibold text-lg">
                    {PRO_CARER_TOOL.title}
                  </h3>
                  <p className="mt-2 text-ink-muted text-sm leading-relaxed">
                    {PRO_CARER_TOOL.body}
                  </p>
                </div>
              </div>
              <Link
                to={PRO_CARER_TOOL.ctaTo}
                className="btn-purple text-sm mt-6 self-start"
                data-testid="faq-hub-tool-shift-toolkit-cta"
              >
                {PRO_CARER_TOOL.ctaLabel}
                <ArrowRight className="w-4 h-4 ml-1" strokeWidth={2} />
              </Link>
            </div>

            <div
              className="card-base p-6 flex flex-col"
              data-testid="faq-hub-tool-pro-pricing"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple flex items-center justify-center shrink-0">
                  <Calculator className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <span
                    className="inline-block text-[11px] font-semibold uppercase tracking-wide
                               text-purple bg-purple-50 rounded-full px-2.5 py-1"
                    data-testid="faq-hub-tool-pro-pricing-badge"
                  >
                    Pro Carer Office members
                  </span>
                  <h3 className="mt-3 font-display font-semibold text-lg">
                    Pricing calculators &mdash; Hourly &amp; Live-In
                  </h3>
                  <p className="mt-2 text-ink-muted text-sm leading-relaxed">
                    Two interactive planners for self-employed carers: an <strong>Hourly Care Rate Starter</strong> and a <strong>Live-In Care Rate Starter</strong>. Work backwards from your income target, factor in unpaid admin, mileage and waking nights, and get a sustainable starting rate you can print or copy.
                  </p>
                </div>
              </div>
              <Link
                to="/pro-carer-circle"
                className="btn-purple text-sm mt-6 self-start"
                data-testid="faq-hub-tool-pro-pricing-cta"
              >
                Learn about Pro Carer Office
                <ArrowRight className="w-4 h-4 ml-1" strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section border-t border-slate-100">
        <div className="container-jcc max-w-4xl text-center">
          <SectionHeading
            eyebrow="Still have a question?"
            title="Take the next step, one clear decision at a time."
            accent="teal"
            align="center"
            description="Independent care can feel like a lot to navigate at first. Start with the guidance, use the practical tools and take things one clear step at a time."
          />
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {CTAS.map((c) => (
              <CtaLink key={c.label} {...c} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
